import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateSupplyPlanQueries } from "./useSupplyPlanQuery";
import {
  activitiesRepository,
  type ActivityLocationPayload,
} from "../repositories/activitiesRepository";

export type ActivityCreatePayload = {
  title: string;
  terreiroId?: string;
  mediumId?: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  isTemplate: boolean;
  isAllDay: boolean;
  minConfirmedMembers?: number | null;
  insumos: Array<{ id: string; quantity: number }>;
  members: string[];
  isInTerreiro?: boolean;
  location?: ActivityLocationPayload | null;
};

export const useCreateActivityMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ActivityCreatePayload) =>
      activitiesRepository.createActivity(payload),
    onSuccess: (_response, variables) => {
      const scopedTerreiroId = variables.terreiroId;
      const scopedMediumId = variables.mediumId;

      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key)) return false;

          if (scopedTerreiroId) {
            return (
              (key[0] === "terreiro" &&
                key[1] === scopedTerreiroId &&
                (key[2] === "activities" || key[2] === "activity")) ||
              (key[0] === "terreiro" &&
                key[1] === scopedTerreiroId &&
                key[2] === "activities" &&
                key[3] === "recent")
            );
          }

          if (scopedMediumId) {
            return (
              (key[0] === "activity" && key[1] === "medium" && key[2] === scopedMediumId) ||
              (key[0] === "personal" && key[1] === "activities" && key[2] === "recent")
            );
          }

          return false;
        },
      });

      // Keep template caches coherent when creation was template-based.
      if (variables.isTemplate) {
        queryClient.invalidateQueries({
          queryKey: ["activities", "models"],
        });
      }

      invalidateSupplyPlanQueries(
        queryClient,
        scopedTerreiroId,
        scopedMediumId,
      );
    },
  });
};
